import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions, 
  Button, 
  Chip, 
  CircularProgress, 
  Paper,
  Divider,
  IconButton,
  Avatar,
  Tab,
  Tabs,
  Alert,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  Place as PlaceIcon,
  Phone as PhoneIcon,
  Restaurant as RestaurantIcon,
  MeetingRoom as EventSpaceIcon,
  Hotel as ResortIcon,
  LocalOffer as PackageIcon,
  BookOnline as BookingIcon,
  TrendingUp as AnalyticsIcon,
  BarChart as ChartIcon,
  StarOutline as StarIcon,
  StarHalf as StarHalfIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useVenue } from '../../contexts/VenueContext';
import { Venue, VenueType } from '../../types/Venue';
import { format } from 'date-fns';
import BookingService from '../../services/bookingService';
import { getPackagesByVenueId } from '../../services/venueService';

// Dashboard tab interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const VenuesList: React.FC = () => {
  const { venues, refreshVenues, isLoading } = useVenue();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [venueMetrics, setVenueMetrics] = useState<{[venueId: string]: {
    packages: number;
    activePackages: number;
    bookings: number;
    pendingBookings: number;
    revenue: number;
  }}>({});

  // Generic venue type images for fallbacks
  const fallbackImages = {
    restaurant: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    event_space: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    resort: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
  };

  const getVenueImage = (venue: Venue) => {
    // Check if venue has a primary image
    if (venue.images?.primary && venue.images.primary.startsWith('http')) {
      return venue.images.primary;
    }
    
    // Return type-specific fallback image
    return fallbackImages[venue.type] || fallbackImages.restaurant;
  };

  const getVenueLogo = (venue: Venue) => {
    // Check if venue has a logo
    if (venue.images?.logo && venue.images.logo.startsWith('http')) {
      return venue.images.logo;
    }
    
    // Default to undefined (no logo) instead of null
    return undefined;
  };

  useEffect(() => {
    const loadVenues = async () => {
      setIsRefreshing(true);
      await refreshVenues();
      setIsRefreshing(false);
    };
    
    loadVenues();
  }, []);

  useEffect(() => {
    const fetchVenueMetrics = async () => {
      const metrics: {[venueId: string]: any} = {};
      
      for (const venue of venues) {
        try {
          // Get packages for this venue
          const packages = await getPackagesByVenueId(venue.id);
          
          // Get bookings for this venue
          const bookings = await BookingService.getVenueBookings(venue.id);
          
          // Calculate metrics
          const pendingBookings = bookings.filter(booking => booking.status === 'pending').length;
          const revenue = bookings
            .filter(booking => booking.status === 'completed' || booking.status === 'confirmed')
            .reduce((total, booking) => total + (booking.amount || 0), 0);
          
          metrics[venue.id] = {
            packages: packages.length,
            activePackages: packages.filter(pkg => pkg.isActive).length,
            bookings: bookings.length,
            pendingBookings,
            revenue
          };
        } catch (error) {
          console.error(`Error fetching metrics for venue ${venue.id}:`, error);
          metrics[venue.id] = {
            packages: 0,
            activePackages: 0,
            bookings: 0,
            pendingBookings: 0,
            revenue: 0
          };
        }
      }
      
      setVenueMetrics(metrics);
    };
    
    if (venues.length > 0) {
      fetchVenueMetrics();
    }
  }, [venues]);

  const getVenueTypeIcon = (type: VenueType) => {
    switch (type) {
      case 'restaurant':
        return <RestaurantIcon />;
      case 'event_space':
        return <EventSpaceIcon />;
      case 'resort':
        return <ResortIcon />;
      default:
        return <StoreIcon />;
    }
  };

  const getVenueTypeLabel = (type: VenueType) => {
    switch (type) {
      case 'restaurant':
        return 'Restaurant';
      case 'event_space':
        return 'Event Space';
      case 'resort':
        return 'Resort';
      default:
        return 'Venue';
    }
  };

  const handleCreateVenue = () => {
    navigate('/venues/create');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleManageVenue = (venueId: string) => {
    navigate(`/venues/${venueId}/dashboard`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Venue Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateVenue}
          sx={{ bgcolor: '#43a047' }}
        >
          Create New Venue
        </Button>
      </Box>
      
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Venues" />
          <Tab label="Active Packages" />
          <Tab label="Performance" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          {isLoading || isRefreshing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : venues.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
              <StoreIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No venues yet
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create your first venue to start offering packages and experiences to your customers.
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 2, bgcolor: '#43a047' }}
                startIcon={<AddIcon />}
                onClick={handleCreateVenue}
              >
                Create Your First Venue
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {venues.map((venue) => (
                <Grid item xs={12} md={6} key={venue.id}>
                  <Card sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                    overflow: 'visible'
                  }}>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="160"
                        image={getVenueImage(venue)}
                        alt={venue.name}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          p: 1,
                          boxShadow: 2,
                        }}
                      >
                        {getVenueTypeIcon(venue.type)}
                      </Box>
                      
                      {getVenueLogo(venue) && (
                        <Avatar
                          src={getVenueLogo(venue)}
                          alt={`${venue.name} logo`}
                          sx={{
                            position: 'absolute',
                            bottom: -20,
                            right: 16,
                            width: 56,
                            height: 56,
                            border: '2px solid white',
                            boxShadow: 2,
                          }}
                        />
                      )}
                    </Box>
                    
                    <CardContent sx={{ pt: 3, flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h5" component="h2" gutterBottom>
                            {venue.name}
                          </Typography>
                          <Chip
                            icon={getVenueTypeIcon(venue.type)}
                            label={getVenueTypeLabel(venue.type)}
                            size="small"
                            sx={{ mb: 1 }}
                          />
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                        <Chip
                          size="small"
                          icon={<PackageIcon fontSize="small" />}
                          label={`${venueMetrics[venue.id]?.activePackages || 0} Active Packages`}
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          icon={<BookingIcon fontSize="small" />}
                          label={`${venueMetrics[venue.id]?.bookings || 0} Bookings`}
                          color="secondary"
                          variant="outlined"
                        />
                        {venueMetrics[venue.id]?.pendingBookings > 0 && (
                          <Chip
                            size="small"
                            icon={<Badge badgeContent={venueMetrics[venue.id]?.pendingBookings} color="error" />}
                            label="Pending Bookings"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {venue.description?.substring(0, 120)}
                        {venue.description?.length > 120 ? '...' : ''}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
                        <PlaceIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {venue.location?.city}, {venue.location?.state}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {venue.contact?.phone}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <Divider />
                    
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.default' }}>
                      <Typography variant="subtitle1" color="primary.dark" fontWeight="bold">
                        {formatCurrency(venueMetrics[venue.id]?.revenue || 0)} Revenue
                      </Typography>
                      
                      <Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AnalyticsIcon />}
                          sx={{ mr: 1, bgcolor: '#43a047' }}
                          onClick={() => handleManageVenue(venue.id)}
                        >
                          Manage
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {venues.length === 0 ? (
            <Alert severity="info">You need to create venues before you can manage packages</Alert>
          ) : (
            <Grid container spacing={3}>
              {venues.map((venue) => (
                venueMetrics[venue.id]?.packages > 0 ? (
                  <Grid item xs={12} md={6} key={venue.id}>
                    <Paper sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {getVenueLogo(venue) ? (
                          <Avatar src={getVenueLogo(venue)} sx={{ mr: 2 }} />
                        ) : (
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {venue.name.charAt(0)}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="h6">{venue.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getVenueTypeLabel(venue.type)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Total Packages
                            </Typography>
                            <Typography variant="h5">{venueMetrics[venue.id]?.packages || 0}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Active Packages
                            </Typography>
                            <Typography variant="h5">{venueMetrics[venue.id]?.activePackages || 0}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                      
                      <Button 
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => navigate(`/venues/${venue.id}/packages`)}
                      >
                        Manage Packages
                      </Button>
                    </Paper>
                  </Grid>
                ) : null
              ))}
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateVenue}
                  sx={{ mt: 2, bgcolor: '#43a047' }}
                >
                  Add New Venue
                </Button>
              </Grid>
            </Grid>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          {venues.length === 0 ? (
            <Alert severity="info">Create venues to see performance metrics</Alert>
          ) : (
            <Grid container spacing={3}>
              {venues.map((venue) => (
                <Grid item xs={12} key={venue.id}>
                  <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getVenueLogo(venue) ? (
                          <Avatar src={getVenueLogo(venue)} sx={{ mr: 2 }} />
                        ) : (
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {venue.name.charAt(0)}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="h6">{venue.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getVenueTypeLabel(venue.type)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ChartIcon />}
                        onClick={() => handleManageVenue(venue.id)}
                      >
                        View Details
                      </Button>
                    </Box>
                    
                    <Divider sx={{ mb: 3 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Total Revenue
                          </Typography>
                          <Typography variant="h5" color="primary.main">
                            {formatCurrency(venueMetrics[venue.id]?.revenue || 0)}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={3}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Total Bookings
                          </Typography>
                          <Typography variant="h5" color="primary.main">
                            {venueMetrics[venue.id]?.bookings || 0}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={3}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Pending Bookings
                          </Typography>
                          <Typography variant="h5" color="warning.main">
                            {venueMetrics[venue.id]?.pendingBookings || 0}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={3}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Active Packages
                          </Typography>
                          <Typography variant="h5" color="success.main">
                            {venueMetrics[venue.id]?.activePackages || 0}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default VenuesList;
